import { Suspense, lazy, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/TextField'
import { Callout } from '@/components/ui/Callout'
import { Wordmark } from '@/components/layout/Wordmark'
import { GanttPreviewCard } from '@/components/layout/GanttPreviewCard'
import { useAuth } from '@/lib/auth/use-auth'
import { AUTH_ERROR_MESSAGE } from '@/lib/auth/auth-context'
import { AuthError, isDemoMode } from '@/lib/auth'

// Dynamic so the mock provider and its demo password never reach a build that
// talks to a real backend.
const DemoPanel = lazy(() => import('@/components/auth/DemoPanel'))

export function LoginPage() {
  const { signIn, lastSignOutReason } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})

  const from = (location.state as { from?: string } | null)?.from ?? '/'

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    const nextFieldErrors: typeof fieldErrors = {}
    if (!email.trim()) nextFieldErrors.email = 'Required'
    if (!password) nextFieldErrors.password = 'Required'
    setFieldErrors(nextFieldErrors)
    if (Object.keys(nextFieldErrors).length > 0) return

    setSubmitting(true)
    try {
      await signIn({ email, password })
      navigate(from, { replace: true })
    } catch (err) {
      setError(
        err instanceof AuthError ? AUTH_ERROR_MESSAGE[err.code] : AUTH_ERROR_MESSAGE.unknown,
      )
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      <div className="flex flex-col px-6 py-8 sm:px-12 lg:px-16">
        <Wordmark />

        <div className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-[400px]">
            <h1 className="t-display-sm text-ink">Sign in</h1>

            {lastSignOutReason && (
              <Callout tone="warning" className="mt-6">
                {lastSignOutReason === 'idle'
                  ? 'Signed out after inactivity.'
                  : 'Session expired.'}
              </Callout>
            )}

            <form onSubmit={handleSubmit} noValidate className="mt-8 space-y-5">
              {error && <Callout tone="error">{error}</Callout>}

              <TextField
                label="Email"
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                inputMode="email"
                value={email}
                error={fieldErrors.email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
              />

              <TextField
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                revealable
                value={password}
                error={fieldErrors.password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
              />

              <Button type="submit" fullWidth loading={submitting}>
                Sign in
              </Button>
            </form>

            {isDemoMode() && (
              <Suspense fallback={null}>
                <DemoPanel
                  onPick={(demoEmail, demoPassword) => {
                    setEmail(demoEmail)
                    setPassword(demoPassword)
                    setError(null)
                    setFieldErrors({})
                  }}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>

      {/* DESIGN.md signature pattern: dark band carrying a product-UI card. */}
      <div className="hidden flex-col justify-center bg-surface-dark px-16 py-16 lg:flex">
        <div className="max-w-[520px]">
          <GanttPreviewCard />
        </div>
      </div>
    </div>
  )
}

